import React, { useState } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { UserRole } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNAButton, DNAModal, DNAText, Modal } from '@alucio/lux-ui';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useDispatch, useSelector } from 'src/state/redux';
import { userSettingsActions } from 'src/state/redux/slice/userSetting';
import { useHistory } from 'src/router';
import { BulletinPost } from './BulletinPost';
import { useBulletinContext } from './BulletinProvider';
import { markBulletinsViewed } from './bulletin.service';
import { useBulletins } from './useBulletins';
import { getUnreadBulletin } from './bulletin.utils';

export const BulletinAnnouncement: React.FC = () => {
  const enabled = useFeatureFlags('BEAC_8524_beacon_bulletin');
  const { bulletins, hydrated, user, name } = useBulletins();
  const isViewer = user?.role === UserRole.TENANT_VIEWER;
  const dispatch = useDispatch();
  const { records: settingsRecords, hydrated: settingsHydrated } = useSelector((state) => state.userSettings);
  const userSettings = settingsRecords.find(
    (settings) => settings.userId === user?.id && settings.tenantId === user?.tenantId,
  );
  const viewedIds = userSettings?.viewedBulletinIds || [];
  const otherModalVisible = useSelector((state) => state.DNAModal.isVisible);
  const { width, height } = useWindowDimensions();
  const history = useHistory();
  const { sessionStartedAt: loginAt, isManagementDialogOpen } = useBulletinContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const unread = bulletins.filter(
    (post) => post.createdBy !== user?.id &&
      Date.parse(post.publishedAt || '') <= loginAt.getTime() && !viewedIds.includes(post.id),
  );
  const post = getUnreadBulletin(unread, viewedIds, loginAt);
  const dismiss = async (all = false, viewHistory = false) => {
    if (!isViewer || !post || !user || busy) return;
    setBusy(true);
    const ids = all ? unread.map((record) => record.id) : [post.id];
    try {
      const saved = await markBulletinsViewed(user.tenantId, user.id, ids);
      dispatch(userSettingsActions.upsert(saved));
      setError('');
      if (viewHistory) history.push('/bulletin');
    } catch {
      setError('Your reading status could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  if (
    !enabled || !isViewer || !hydrated || !settingsHydrated || !userSettings ||
    otherModalVisible || isManagementDialogOpen || !post
  ) return null;
  return (
    <Modal
      visible
      backdropStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      style={{
        width: Math.min(800, width - 32),
        height: Math.min(650, height - 48),
        maxHeight: 'calc(var(--beacon-viewport-height, 100dvh) - 48px)',
      }}
    >
      <DNAModal style={{ paddingVertical: 16 }}>
        <DNAModal.Header onClose={() => dismiss(true)} disabled={busy}>
          <DNABox appearance="col" spacing="xs">
            <DNAText h5>{name}</DNAText>
            {unread.length > 1 && <DNAText status="tertiary">{unread.length} new posts</DNAText>}
          </DNABox>
        </DNAModal.Header>
        <ScrollView
          style={{ flex: 1, minHeight: 0 }}
          contentContainerStyle={{ padding: 16 }}
        >
          <DNABox appearance="col" spacing="md">
            <BulletinPost bulletin={post} />
            {!!error && (
              <DNAText status="danger" accessibilityRole="alert">
                {error}
              </DNAText>
            )}
          </DNABox>
        </ScrollView>
        <DNAModal.Confirmation>
          <DNAButton appearance="outline" disabled={busy} onPress={() => dismiss(true, true)}>
            View all posts
          </DNAButton>
          <DNAButton disabled={busy} onPress={() => dismiss()}>
            {unread.length > 1 ? 'Next post' : 'Dismiss'}
          </DNAButton>
        </DNAModal.Confirmation>
      </DNAModal>
    </Modal>
  );
};
